import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import axios, { isCancel } from 'axios';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactSelectInput } from '..';
const valueToIdParams = (value) => {
    if (typeof value === 'object' && value !== null && 'id' in value) {
        return value.id;
    }
    return value;
};
export const ReactSelectAsyncWithAxios = (_a) => {
    var { creatable, dataParams, defaultValue, formatOptions = (options) => options, headers, id, initialLoadOptions = false, label, loadOptionsParams = {}, loadOptionsUrl, multiple = false, name, onChange, value } = _a, otherProps = __rest(_a, ["creatable", "dataParams", "defaultValue", "formatOptions", "headers", "id", "initialLoadOptions", "label", "loadOptionsParams", "loadOptionsUrl", "multiple", "name", "onChange", "value"]);
    const mounted = useRef(false);
    const cancelTokenRef = useRef(null);
    const [selectValue, setSelectValue] = useState(defaultValue);
    const [defaultOptions, setDefaultOptions] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const cancelRequest = useCallback(() => { var _a; return (_a = cancelTokenRef.current) === null || _a === void 0 ? void 0 : _a.cancel('Request canceled'); }, []);
    const handleLoadOptions = useCallback((inputValue) => {
        cancelRequest();
        const cancelToken = axios.CancelToken;
        cancelTokenRef.current = cancelToken.source();
        const catchCancel = (err) => {
            if (isCancel(err)) {
                return [];
            }
            return err;
        };
        if (initialLoadOptions ||
            (typeof inputValue === 'string' && inputValue !== '')) {
            setIsLoading(true);
            return axios(Object.assign({ cancelToken: cancelTokenRef.current.token, headers, url: loadOptionsUrl }, (dataParams
                ? { data: Object.assign(Object.assign({}, dataParams), { term: inputValue }) }
                : {
                    params: Object.assign(Object.assign({ size: 100 }, loadOptionsParams), { term: inputValue }),
                })))
                .then(({ data = [] }) => {
                if (mounted.current) {
                    setDefaultOptions(formatOptions(data));
                }
                setIsLoading(false);
                return formatOptions(data);
            })
                .catch(catchCancel);
        }
        const effectiveValue = value || defaultValue;
        if (effectiveValue) {
            const getParamsMultipleId = (items) => items.includes(undefined) ? [] : items.map(valueToIdParams);
            const paramsId = multiple
                ? getParamsMultipleId(effectiveValue)
                : valueToIdParams(effectiveValue);
            const hasId = multiple ? paramsId.length > 0 : paramsId !== null;
            return hasId
                ? axios(Object.assign({ cancelToken: cancelTokenRef.current.token, headers, url: loadOptionsUrl }, (dataParams
                    ? { data: Object.assign(Object.assign({}, dataParams), { id: paramsId }) }
                    : {
                        params: Object.assign(Object.assign({}, loadOptionsParams), { id: paramsId }),
                    })))
                    .then(({ data }) => {
                    const options = Array.isArray(data) ? data : [data];
                    let newValue = data;
                    if (multiple && !Array.isArray(data)) {
                        newValue = [data];
                    }
                    if (mounted.current) {
                        setDefaultOptions(formatOptions(options));
                        setSelectValue(newValue);
                    }
                    return formatOptions(options);
                })
                    .catch(catchCancel)
                : Promise.resolve([]);
        }
        return Promise.resolve([]);
    }, [
        dataParams,
        defaultValue,
        initialLoadOptions,
        loadOptionsParams,
        loadOptionsUrl,
        multiple,
        value,
    ]);
    const handleChange = useCallback((option) => {
        setSelectValue(option);
        if (onChange) {
            onChange(option);
        }
    }, [onChange]);
    useEffect(() => {
        let arrayValues;
        let arraySelectValues;
        if (multiple) {
            arrayValues =
                !value || value.includes(undefined)
                    ? []
                    : value.map(valueToIdParams).sort();
            arraySelectValues = selectValue
                ? selectValue.map(valueToIdParams).sort()
                : [];
        }
        else {
            arrayValues = [value];
            arraySelectValues = selectValue ? [selectValue.id] : [];
        }
        if (!isEqual(arrayValues.map(String), arraySelectValues.map(String))) {
            handleLoadOptions();
        }
    }, [defaultValue, multiple, selectValue, value]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            cancelRequest();
        };
    }, []);
    return (_jsx(ReactSelectInput, Object.assign({}, otherProps, { cacheOptions: true, defaultOptions: defaultOptions, defaultValue: defaultValue, id: id, isAsync: true, isCreatable: creatable, isLoading: isLoading, isMulti: multiple, label: label, loadOptions: handleLoadOptions, name: name, onChange: handleChange, value: selectValue })));
};
//# sourceMappingURL=ReactSelectAsyncWithAxios.js.map